import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import Seo from "@/components/Seo";
import { CONSULTING_SERVICES, SEO_DEFAULTS } from "@/data/content";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = useMemo(() => CONSULTING_SERVICES.find((s) => s.slug === slug), [slug]);

  if (!service) {
    return (
      <div className="pt-40 pb-24 px-6 text-center text-[#3F4A6B]">
        <Seo title="Service not found" noindex />
        <div className="font-display text-3xl mb-4">Service not found.</div>
        <Link to="/services" className="text-gold underline">Return to services</Link>
      </div>
    );
  }

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: service.name,
    description: service.description,
    provider: { "@type": "Organization", name: "AlchamisGroup", url: SEO_DEFAULTS.baseUrl },
    areaServed: service.industries,
    serviceType: service.category,
  };

  return (
    <div data-testid={`service-detail-${service.slug}`} className="pt-32 pb-24">
      <Seo
        title={service.name}
        description={service.summary}
        path={`/services/${service.slug}`}
        jsonLd={jsonLd}
      />

      <section className="max-w-[1400px] mx-auto px-6 md:px-12">
        <Link to="/services" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#6B7390] hover:text-gold">
          <ArrowLeft size={14} /> All Practices
        </Link>
        <div className="mt-10 grid grid-cols-12 gap-8 items-end">
          <div className="col-span-12 lg:col-span-8">
            <div className="number-badge">{service.code} · {service.category}</div>
            <h1 className="font-display text-5xl sm:text-7xl text-[#001B54] mt-6 leading-[1.02]">{service.name}</h1>
            <p className="text-gold/90 text-sm tracking-[0.18em] uppercase mt-4">{service.summary}</p>
            <p className="mt-8 text-[#3F4A6B] text-lg leading-relaxed max-w-2xl">{service.description}</p>
          </div>
          <div className="col-span-12 lg:col-span-4">
            <div className="bg-white border border-[#001B54]/10 shadow-[0_24px_60px_-30px_rgba(0,27,84,0.18)] p-8">
              <div className="overline mb-4">Engagement Outcomes</div>
              <ul className="space-y-3 text-sm text-[#0A1228]">
                {service.outcomes.map((o) => (
                  <li key={o} className="flex gap-3 items-start">
                    <Check size={14} className="text-gold mt-1 shrink-0" /> {o}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="mt-20 max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-12 gap-8">
        <div className="col-span-12 lg:col-span-5">
          <span className="overline">Benefits</span>
          <h2 className="font-display text-4xl text-[#001B54] mt-6 leading-tight">Why leaders engage us.</h2>
        </div>
        <div className="col-span-12 lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-px bg-[#001B54]/8">
          {service.benefits.map((b) => (
            <div key={b} className="bg-white p-6">
              <div className="text-[#0A1228] text-sm leading-relaxed">{b}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-20 max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-12 gap-8">
        <div className="col-span-12 lg:col-span-6">
          <span className="overline">Use Cases</span>
          <h2 className="font-display text-4xl text-[#001B54] mt-6 leading-tight">When clients call.</h2>
          <ul className="mt-8 space-y-4">
            {service.use_cases.map((u, i) => (
              <li key={u} className="flex gap-4 items-start border-b border-[#001B54]/10 pb-4">
                <span className="number-badge mt-1">{String(i + 1).padStart(2, "0")}</span>
                <span className="text-[#0A1228] leading-relaxed">{u}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="col-span-12 lg:col-span-5 lg:col-start-8">
          <span className="overline">Deliverables</span>
          <h2 className="font-display text-4xl text-[#001B54] mt-6 leading-tight">What you receive.</h2>
          <ul className="mt-8 space-y-3">
            {service.deliverables.map((d) => (
              <li key={d} className="flex gap-3 items-center border border-[#001B54]/10 p-4">
                <span className="w-1.5 h-1.5 bg-gold" /> <span className="text-[#0A1228] text-sm">{d}</span>
              </li>
            ))}
          </ul>
          <div className="mt-10">
            <div className="overline mb-3">Industries</div>
            <div className="flex flex-wrap gap-2">
              {service.industries.map((i) => (
                <span key={i} className="px-3.5 py-2 border border-gold/30 text-[0.7rem] tracking-[0.14em] uppercase text-gold/90">{i}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="mt-24 max-w-[1100px] mx-auto px-6 md:px-12 text-center">
        <h2 className="font-display text-4xl sm:text-5xl text-[#001B54] leading-tight">
          Engage the {service.name} practice.
        </h2>
        <p className="text-[#3F4A6B] mt-6 max-w-xl mx-auto">
          Begin with a private diagnostic or request a partner-led conversation.
        </p>
        <div className="mt-8 flex justify-center gap-4 flex-wrap">
          <Link to="/ai-advisor" className="luxury-button" data-testid="service-cta-advisor">Begin Diagnostic <ArrowRight size={14} /></Link>
          <Link to="/contact" className="luxury-button-ghost">Speak to Partner</Link>
        </div>
      </section>
    </div>
  );
}
